import {
  Fiber,
  Hook,
  Key,
  MutableRefObject,
  Props,
  ReactElement,
  ReactNode,
} from "../types";

import {
  arrayfy,
  equals,
  isArray,
  isFn,
  isObj,
  shallowEqual,
} from "../utils/utils";
import {
  DELETION,
  NONE,
  NullElement,
  PLACEMENT,
  TEXT_ELEMENT,
  UPDATE,
} from "../constants";
import { initTask } from "../schedule";
import { commitUpdate, pushDeletion } from "./commit";
import { renderWithHooks } from "../hooks";

function reconcileChildren(fiber: Fiber, newFibers: Fiber[]) {
  let oldFiber = fiber?.alternate?.child;

  const newKeys = new Map<Key, Fiber>();
  for (const item of newFibers) if (item.key) newKeys.set(item.key, item);

  let index = 0;
  let prevSibling: Fiber | undefined;

  while (index < newFibers!.length || oldFiber != undefined) {
    const element = newFibers[index] as any;

    if (oldFiber) {
      // Check if the element key is the same as the oldFiber
      let sametype =
        element &&
        equals(element.type, oldFiber.type) &&
        equals(element.props?.key, oldFiber.key);

      // If the element is the same type and key as the oldFiber, update it
      if (sametype) {
        newFibers[index] = updateFiber(newFibers[index], oldFiber, fiber);
      } else {
        const oldKey = oldFiber?.key;
        const elementFounded = oldKey && newKeys.get(oldKey);

        if (!elementFounded) {
          deleteFiber(oldFiber);

          if (element) {
            cloneDom(newFibers[index], oldFiber, fiber);
          }
          oldFiber = oldFiber.sibling;
          continue;
        } else {
          newFibers[index] = updateFiber(newFibers[index], oldFiber, fiber);
        }
      }

      oldFiber = oldFiber.sibling;
    } else {
      if (newFibers[index]) {
        // If there is no oldFiber, create a new one
        newFibers[index] = placeFiber(newFibers[index], fiber);
      }
    }

    if (newFibers[index]) {
      newFibers[index].parent = fiber;
      if (index > 0) {
        if (prevSibling) {
          prevSibling.sibling = newFibers[index];
        }
      } else {
        fiber.child = newFibers[index];
      }
      prevSibling = newFibers[index];
    }

    index++;
  }
}

const updateFiber = (element: Fiber, oldFiber: Fiber, parent: Fiber) =>
  updateElement(element, oldFiber, parent, false);

const cloneDom = (element: Fiber, oldFiber: Fiber, parent: Fiber) =>
  updateElement(element, oldFiber, parent, true);

const placeFiber = (element: Fiber, parent: Fiber) =>
  updateElement(element, null, parent);

function updateElement(
  element: Fiber,
  oldFiber: Fiber | null,
  parent: Fiber,
  cloneDom = false
): Fiber {
  element.parent = parent;

  if (cloneDom) {
    element.dom = oldFiber!.dom;
    element.flag = NONE;
  } else if (!equals(oldFiber, null)) {
    const equal = shallowEqual(oldFiber?.props, element.props);

    element.alternate = oldFiber;
    element.dom = oldFiber!.dom;
    element.hook = oldFiber!.hook;
    element.effect = oldFiber!.effect;
    element.key = element.props?.key;
    element.flag = equal ? NONE : UPDATE;
  } else {
    element.dom = null;
    element.flag = element.type == NullElement ? NONE : PLACEMENT;
    element.key = element.props?.key;
  }

  return element;
}

function deleteFiber(oldFiber: Fiber) {
  oldFiber.flag = DELETION;
  pushDeletion(oldFiber);
}

export function beginWork(workInProgress: Fiber): void {
  if (isFn(workInProgress.type)) {
    updateFunctionComponent(workInProgress);
  } else {
    updateNormalComponent(workInProgress);
  }
}

function updateNormalComponent(fiber: Fiber) {
  if (!fiber.dom && !equals(fiber.type, NullElement)) {
    fiber.dom = createDom(fiber) as any;
  }

  const currentValue = fiber?.props?.children; // getChildren(fiber);

  reconcileChildren(fiber, currentValue);
}

function updateFunctionComponent(fiber: Fiber) {
  const result = renderWithHooks(fiber, fiber.type);
  const currentValue = arrayfy(result);

  reconcileChildren(fiber, currentValue);
}

let rootFiber: Fiber | undefined;

export function render(element: ReactNode, container: HTMLElement | null) {
  const children = [element];
  const currentRoot: Fiber = {
    dom: container,
    props: { children: children },
  };

  if (rootFiber?.alternate != null) {
    const previousRoot = rootFiber.alternate;
    previousRoot.alternate = rootFiber;
    previousRoot.props = currentRoot.props;
    rootFiber = previousRoot;
  } else {
    if (rootFiber != null) currentRoot.alternate = rootFiber;
    rootFiber = currentRoot;
  }
  // cleanDeletions();
  initTask(rootFiber);
}

function createDom(fiber: Fiber): Node {
  const dom = equals(fiber.type, TEXT_ELEMENT)
    ? document.createTextNode(fiber.props.textContent)
    : (document.createElement(fiber.type as string) as any);

  commitUpdate(dom, {}, fiber.props);

  return dom;
}

export function createElement(
  type: string,
  props: Props,
  ...children: any[]
): ReactNode {
  const childElements = children.reduce((acc, child) => {
    if (child === undefined || child === null || child === false) {
      acc.push(createElement(NullElement, {}));
    } else if (isObj(child)) {
      if (isArray(child)) {
        child.forEach((item) => {
          acc.push(item);
        });
      } else {
        acc.push(child);
      }
    } else {
      acc.push(
        createElement(TEXT_ELEMENT, {
          textContent: `${child}`,
        })
      );
    }

    return acc;
  }, []);

  const key = props?.key;
  const ref = props?.ref;

  return {
    key,
    ref,
    type,
    props: { ...props, children: childElements },
  };
}

export function Fragment(props: any) {
  return props.children;
}
